"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReleaseUpdateWatcher = exports.useReleaseUpdate = exports.wipeStalePageCache = exports.CACHE_WIPE_KEY = exports.CACHE_WIPE_ID = exports.shouldReloadTo = exports.fetchLiveMainBundle = exports.getRunningMainBundle = exports.getMainBundle = exports.canReloadOn = exports.NO_RELOAD_ON = void 0;
var tslib_1 = require("tslib");
var react_1 = require("react");
var react_router_dom_1 = require("react-router-dom");
/**
 * Moves open tabs onto a new release.
 *
 * A tab keeps running the build it was loaded with. After a release, it can
 * keep that old code for hours (or days, if the browser kept an old copy of
 * the page), including bugs we've already fixed. This compares the running
 * build with the one the server now serves and, when they differ, reloads the
 * page on the user's next navigation - never in the middle of a page, so a
 * half-filled form isn't lost.
 *
 * Builds are identified by the hashed main bundle name in index.html
 * (/static/js/main.<hash>.chunk.js), which changes with every release. In
 * development the bundle has no hash, so this does nothing.
 */
var CHECK_INTERVAL_MS = 5 * 60 * 1000;
var RELOAD_GUARD_KEY = 'tmz.releaseReloadedTo';
var MAIN_BUNDLE = /\/static\/js\/main\.[0-9a-f]+\.chunk\.js/;
/**
 * Pages that act as soon as they open. The reload runs after the next page
 * has started rendering in the old build, so reloading into one of these
 * would run its action twice - /checkout places free download orders on
 * open (a duplicate order), /impersonate starts a token exchange. A pending
 * reload waits for the navigation after them instead.
 */
exports.NO_RELOAD_ON = ['/checkout', '/impersonate'];
/** Whether a pending release may reload the page now that it's on `pathname`. */
var canReloadOn = function (pathname, noReloadOn) {
    if (noReloadOn === void 0) { noReloadOn = exports.NO_RELOAD_ON; }
    return !noReloadOn.some(function (page) { return pathname === page || pathname.startsWith("".concat(page, "/")); });
};
exports.canReloadOn = canReloadOn;
/** The main bundle path in a page or script URL, or null if there isn't one. */
var getMainBundle = function (text) { var _a, _b; return (_b = (_a = text === null || text === void 0 ? void 0 : text.match(MAIN_BUNDLE)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null; };
exports.getMainBundle = getMainBundle;
/** The main bundle of the build this tab is running. */
var getRunningMainBundle = function () {
    var _a;
    return (_a = Array.from(document.scripts)
        .map(function (script) { return (0, exports.getMainBundle)(script.getAttribute('src')); })
        .find(function (bundle) { return bundle !== null; })) !== null && _a !== void 0 ? _a : null;
};
exports.getRunningMainBundle = getRunningMainBundle;
/** The main bundle of the build the server serves now (bypassing caches). */
var fetchLiveMainBundle = function () { return (0, tslib_1.__awaiter)(void 0, void 0, void 0, function () {
    var response, _a, _b;
    var _c;
    return (0, tslib_1.__generator)(this, function (_d) {
        switch (_d.label) {
            case 0: return [4 /*yield*/, fetch("".concat((_c = process.env.PUBLIC_URL) !== null && _c !== void 0 ? _c : '', "/index.html"), {
                    cache: 'no-store',
                })];
            case 1:
                response = _d.sent();
                if (!response.ok) return [3 /*break*/, 3];
                _b = exports.getMainBundle;
                return [4 /*yield*/, response.text()];
            case 2:
                _a = _b.apply(void 0, [_d.sent()]);
                return [3 /*break*/, 4];
            case 3:
                _a = null;
                _d.label = 4;
            case 4: return [2 /*return*/, _a];
        }
    });
}); };
exports.fetchLiveMainBundle = fetchLiveMainBundle;
/**
 * Reload onto `live` only when both builds are known and differ, and not if
 * this tab already reloaded onto that same build. The last rule stops a
 * reload loop if, mid-rollout, the server briefly serves both versions.
 */
var shouldReloadTo = function (running, live, alreadyReloadedTo) {
    return running !== null &&
        live !== null &&
        running !== live &&
        alreadyReloadedTo !== live;
};
exports.shouldReloadTo = shouldReloadTo;
var readReloadGuard = function () {
    try {
        return sessionStorage.getItem(RELOAD_GUARD_KEY);
    }
    catch (_a) {
        return null;
    }
};
var writeReloadGuard = function (bundle) {
    try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, bundle);
    }
    catch (_a) {
        // Without storage the loop guard is off; reloading is still correct.
    }
};
/**
 * Change to wipe every browser's saved pages again on its next visit.
 * 2026-10-15: pages saved before cache headers existed (2026-10-14) were kept
 * for days per address, each still running the August build.
 */
exports.CACHE_WIPE_ID = '2026-10-15';
exports.CACHE_WIPE_KEY = 'tmz.cacheWipe';
/**
 * Once per browser, ask the server to drop every page it has saved for this
 * site (nginx /__clear-cache answers with Clear-Site-Data: "cache"). A refresh
 * only replaces the page refreshed; this clears the old copies of all the
 * others, so new tabs and links load the current build too. Only the HTTP
 * cache is cleared - logins, carts and settings are untouched. Returns
 * whether the wipe was done now; a failed attempt is retried next load.
 */
var wipeStalePageCache = function () { return (0, tslib_1.__awaiter)(void 0, void 0, void 0, function () {
    var response, _a;
    var _b;
    return (0, tslib_1.__generator)(this, function (_c) {
        switch (_c.label) {
            case 0:
                _c.trys.push([0, 2, , 3]);
                if (localStorage.getItem(exports.CACHE_WIPE_KEY) === exports.CACHE_WIPE_ID) {
                    return [2 /*return*/, false];
                }
                return [4 /*yield*/, fetch("".concat((_b = process.env.PUBLIC_URL) !== null && _b !== void 0 ? _b : '', "/__clear-cache"), {
                        cache: 'no-store',
                    })];
            case 1:
                response = _c.sent();
                if (!response.ok) {
                    return [2 /*return*/, false];
                }
                localStorage.setItem(exports.CACHE_WIPE_KEY, exports.CACHE_WIPE_ID);
                return [2 /*return*/, true];
            case 2:
                _a = _c.sent();
                return [2 /*return*/, false];
            case 3: return [2 /*return*/];
        }
    });
}); };
exports.wipeStalePageCache = wipeStalePageCache;
var useReleaseUpdate = function () {
    var pathname = (0, react_router_dom_1.useLocation)().pathname;
    var running = (0, react_1.useRef)((0, exports.getRunningMainBundle)());
    var pendingRelease = (0, react_1.useRef)(null);
    // The page was just loaded, so it's current; first check after an interval.
    var lastCheck = (0, react_1.useRef)(Date.now());
    // Production builds only: the dev server has no /__clear-cache.
    (0, react_1.useEffect)(function () {
        if (running.current) {
            (0, exports.wipeStalePageCache)();
        }
    }, []);
    var check = (0, react_1.useCallback)(function () { return (0, tslib_1.__awaiter)(void 0, void 0, void 0, function () {
        var live, _a;
        return (0, tslib_1.__generator)(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!running.current || pendingRelease.current) {
                        return [2 /*return*/];
                    }
                    if (Date.now() - lastCheck.current < CHECK_INTERVAL_MS) {
                        return [2 /*return*/];
                    }
                    lastCheck.current = Date.now();
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, (0, exports.fetchLiveMainBundle)()];
                case 2:
                    live = _b.sent();
                    if ((0, exports.shouldReloadTo)(running.current, live, readReloadGuard())) {
                        pendingRelease.current = live;
                    }
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, []);
    // On navigation: move onto a release found earlier, otherwise look for one.
    (0, react_1.useEffect)(function () {
        if (pendingRelease.current) {
            if ((0, exports.canReloadOn)(pathname)) {
                writeReloadGuard(pendingRelease.current);
                window.location.reload();
            }
            return;
        }
        check();
    }, [pathname, check]);
    // Coming back to a tab that was left open is when a release is most likely.
    (0, react_1.useEffect)(function () {
        var onVisibilityChange = function () {
            if (document.visibilityState === 'visible') {
                check();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return function () {
            return document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [check]);
};
exports.useReleaseUpdate = useReleaseUpdate;
/** Drop-in component for App.tsx, inside the router. Renders nothing. */
var ReleaseUpdateWatcher = function () {
    (0, exports.useReleaseUpdate)();
    return null;
};
exports.ReleaseUpdateWatcher = ReleaseUpdateWatcher;
